'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

const navItems = [
  {
    key: 'nav-resume',
    href: '/',
    label: 'RÉSUMÉ',
    icon: (active: boolean) => (
      <svg className={`w-5 h-5 transition-all ${active ? 'text-blue-600' : 'text-slate-400'}`} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
      </svg>
    ),
  },
  {
    key: 'nav-pistes',
    href: '/pistes-vent',
    label: 'PISTES',
    icon: (active: boolean) => (
      <svg className={`w-5 h-5 transition-all ${active ? 'text-blue-600' : 'text-slate-400'}`} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
      </svg>
    ),
  },
  {
    key: 'nav-meteo',
    href: '/m-t-o',
    label: 'MÉTÉO',
    icon: (active: boolean) => (
      <svg className={`w-5 h-5 transition-all ${active ? 'text-blue-600' : 'text-slate-400'}`} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"/>
      </svg>
    ),
  },
  {
    key: 'nav-performance',
    href: '/performance',
    label: 'VIGIL.',
    icon: (active: boolean) => (
      <svg className={`w-5 h-5 transition-all ${active ? 'text-blue-600' : 'text-slate-400'}`} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
      </svg>
    ),
  },
  {
    key: 'nav-metar',
    href: '/metar-taf',
    label: 'METAR',
    icon: (active: boolean) => (
      <svg className={`w-5 h-5 transition-all ${active ? 'text-blue-600' : 'text-slate-400'}`} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
      </svg>
    ),
  },
  {
    key: 'nav-cameras',
    href: '/cam-ras',
    label: 'CAMÉRAS',
    icon: (active: boolean) => (
      <svg className={`w-5 h-5 transition-all ${active ? 'text-blue-600' : 'text-slate-400'}`} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
        <circle cx="12" cy="13" r="3" strokeWidth="2"/>
      </svg>
    ),
  },
];

export default function BottomNav() {
  const pathname = usePathname();

  return (
    <nav
      className="shrink-0 flex items-stretch bg-white border-t border-slate-200"
      style={{
        boxShadow: '0 -1px 8px rgba(30,58,95,0.06)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {navItems?.map((item) => {
        const isActive = item?.href === '/' ? pathname === '/' : pathname === item?.href;
        return (
          <Link
            key={item?.key}
            href={item?.href}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 relative transition-all duration-150 py-2 min-h-[52px] ${
              isActive ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            {/* Active background pill */}
            {isActive && (
              <div className="absolute inset-x-1 inset-y-1 bg-blue-50 rounded-xl border border-blue-100"/>
            )}
            <span className={`relative z-10 transition-transform duration-150 ${isActive ? 'scale-110' : ''}`}>
              {item?.icon(isActive)}
            </span>
            <span
              className={`relative z-10 font-bold leading-tight tracking-wide ${isActive ? 'text-blue-600' : 'text-slate-400'}`}
              style={{ fontSize: '0.55rem' }}
            >
              {item?.label}
            </span>
            {/* Active top indicator */}
            {isActive && (
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-blue-500 rounded-b-full"/>
            )}
          </Link>
        );
      })}
    </nav>
  );
}